// Backend-accurate pricing calculator (mirrors ms-service PriceCalculator.java)
// Implements savings (including term-based and hourly overflow logic) and tax-on-tax

// Round to 2 decimals like org.apache.commons.math3.util.Precision.round
const round2 = (num) => Math.round((Number(num) || 0) * 100) / 100;

// Normalize charging type to match backend (Hourly, Daily, Weekly, Monthly, Block)
export const normalizeChargingType = (type = '') => {
	const t = String(type).toLowerCase();
	switch (t) {
		case 'hour':
		case 'hourly':
			return 'Hourly';
		case 'day':
		case 'daily':
			return 'Daily';
		case 'week':
		case 'weekly':
			return 'Weekly';
		case 'month':
		case 'monthly':
			return 'Monthly';
		case 'block':
			return 'Block';
		default:
			return 'Daily';
	}
};

// Known discount/saving ID to name mapping from backend
const KNOWN_SAVINGS = {
	'5e7cb0aeaac4f142d8075f85': 'Deal',          // MS Discount
	'5e7cb0aeaac4f142d8075f86': 'Host Discount'  // Host Discount
};

// Helper to get saving name from ID
const getSavingNameById = (id) => {
	if (!id) return 'Discount';
	const savingId = String(id);
	return KNOWN_SAVINGS[savingId] || 'Discount';
};

// Build savings list from space event payload for a charging type
export const getSavingsForChargingType = (spaceDetails, chargingType) => {
	const ct = normalizeChargingType(chargingType);
	const event = spaceDetails?.events?.[0];
	const results = [];

	// 1) Work/Office style: event.spaceSavings[ { chargingType, savings: [ {terms, amount, saving{...}} ] } ]
	const spaceSavings = event?.spaceSavings || [];
	const match = spaceSavings.find((ss) => String(ss?.chargingType || '').toLowerCase() === ct.toLowerCase());
	if (match && Array.isArray(match.savings)) {
		match.savings.forEach((it) => {
			const savingId = it?.saving?.id || it?.savingId || it?.id || undefined;
			results.push({
				terms: Number(it?.terms ?? it?.saving?.terms ?? 0),
				amount: Number(it?.amount ?? 0),
				saving: {
					id: savingId,
					name: it?.saving?.name || it?.name || getSavingNameById(savingId),
					amountType: (it?.saving?.amountType || it?.amountType || 'PERCENTAGE'),
				},
			});
		});
	}

	// 2) Meet/Train style: event.chargings[].discounts[] with fields { id, term, amount }
	const chargings = event?.chargings || [];
	// Try to find a charging that matches current type by name
	let charging = chargings.find((ch) => (ch?.chargingType?.name || '').toLowerCase() === ct.toLowerCase());
	if (!charging && chargings.length === 1) charging = chargings[0];
	if (charging && Array.isArray(charging.discounts)) {
		charging.discounts.forEach((d) => {
			const discountId = d?.id || d?._id;
			results.push({
				terms: Number(d?.term ?? d?.terms ?? 0),
				amount: Number(d?.amount ?? 0),
				saving: {
					id: discountId,
					name: d?.name || getSavingNameById(discountId),
					amountType: 'PERCENTAGE',
				},
			});
		});
	}

	return results;
};

// Build taxes array with nested taxOnTax, if available; otherwise return empty
export const getTaxesFromSpace = (spaceDetails) => {
	const taxes = spaceDetails?.applicableTaxes || [];
	// If taxes contain taxAmount, assume full objects, else return [] to trigger fallback
	const hasDetails = taxes.some((t) => t && (t.taxAmount !== undefined || t.taxOnTax));
	if (!hasDetails) return [];

	const cloneTax = (t) => ({
		id: t?.id || t?._id || t?.taxId,
		taxName: t?.taxName || t?.name,
		taxAmount: Number(t?.taxAmount ?? t?.rate ?? t?.percentage ?? t?.value ?? 0),
		taxOnTax: Array.isArray(t?.taxOnTax) ? t.taxOnTax.map(cloneTax) : [],
	});

	return taxes.map(cloneTax);
};

// Calculate package price: multiply by duration except for Hourly/Block
const calcPackagePrice = (packagePrice = 0, duration = 0, chargingType = 'Daily') => {
	const ct = normalizeChargingType(chargingType);
	const base = Number(packagePrice) || 0;
	if (ct === 'Block' || ct === 'Hourly') return base;
	return base * (Number(duration) || 0);
};

// Calculate savings total and breakdown (backend logic)
export const calcSavings = ({ price, duration, guest, chargingType, packagePrice = 0, savingsInfo = [] }) => {
	const ct = normalizeChargingType(chargingType);
	const dur = Number(duration) || 0;
	const unitPrice = Number(price) || 0;
	const guests = Number(guest) || 0;

	// basePriceCalculator(): price * duration * guest
	const baseTotal = unitPrice * dur * guests;
	// basePriceCalculatorWithoutDuration(): price * guest
	const baseNoDuration = unitPrice * guests;
	const pkgTotal = calcPackagePrice(packagePrice, dur, ct);

	let totalSaving = 0;
	const mapById = {};
	const breakdown = [];

	(savingsInfo || []).forEach((s) => {
		const terms = Number(s?.terms ?? 0);
		const amount = Number(s?.amount ?? 0);
		const name = s?.saving?.name || '';
		const id = s?.saving?.id || undefined;
		const amountType = (s?.saving?.amountType || 'PERCENTAGE').toUpperCase();

		let savingAmount = 0;
		if (dur >= terms && terms > 0) {
			if (amountType === 'VALUE') {
				savingAmount = amount;
			} else if (amountType === 'PERCENTAGE') {
				if (ct === 'Hourly') {
					// overflow = duration - terms; afterTermPrice = baseNoDuration * (overflow + 1)
					const overflow = dur - terms;
					const afterTermPrice = baseNoDuration * (overflow + 1);
					savingAmount = (afterTermPrice + pkgTotal) * (amount / 100);
				} else {
					savingAmount = (baseTotal + pkgTotal) * (amount / 100);
				}
			}
		}

		savingAmount = round2(savingAmount);
		totalSaving = round2(totalSaving + savingAmount);
		if (id) mapById[id] = savingAmount;
		if (savingAmount > 0) breakdown.push({ name, percentage: amount, value: savingAmount, amountType });
	});

	return { totalSaving: round2(totalSaving), savingsById: mapById, breakdown, baseTotal: round2(baseTotal), baseNoDuration: round2(baseNoDuration), pkgTotal: round2(pkgTotal) };
};

// Recursively compute tax-on-tax against a given base
const computeTaxOnBase = (base, taxes, recordMap, isTopLevel = true) => {
	let total = 0;
	if (!Array.isArray(taxes) || taxes.length === 0) return 0;
	taxes.forEach((t) => {
		const nested = computeTaxOnBase(base, t.taxOnTax || [], recordMap, false);
		const amt = round2((base + nested) * ((Number(t.taxAmount) || 0) / 100));
		total = round2(total + amt);
		if (isTopLevel && t.id) recordMap[t.id] = amt;
	});
	return round2(total);
};

// Calculate full pricing like backend PriceCalculator.getPrice()
export const calculateBackendPricing = ({
	unitRate,
	duration,
	guestCount,
	chargingType,
	packagePrice = 0,
	savingsInfo = [],
	taxes = [],
	promoCode = null,
}) => {
	const ct = normalizeChargingType(chargingType);
	const dur = Number(duration) || 0;
	const guests = Number(guestCount) || 0;
	const price = Number(unitRate) || 0;

	// Base totals
	const baseTotal = round2(price * dur * guests);
	const pkgTotal = round2(calcPackagePrice(packagePrice, dur, ct));

	// Savings
	const { totalSaving, breakdown } = calcSavings({ price, duration: dur, guest: guests, chargingType: ct, packagePrice, savingsInfo });

	// Promo code discount (percentage/value on (base+pkg)) – optional
	let promoDiscount = 0;
	if (promoCode && promoCode.amount) {
		const type = (promoCode.amountType || 'PERCENTAGE').toUpperCase();
		if (type === 'VALUE') promoDiscount = round2(promoCode.amount);
		if (type === 'PERCENTAGE') promoDiscount = round2((baseTotal + pkgTotal) * (promoCode.amount / 100));
	}

	// Base price after savings
	const baseAfterSavings = round2(baseTotal + pkgTotal - (totalSaving + promoDiscount));

	// Taxes
	const taxMap = {};
	const totalTax = round2(computeTaxOnBase(baseAfterSavings, taxes, taxMap, true));

	const finalPrice = round2(baseAfterSavings + totalTax);

	return {
		bookingTotal: baseTotal,
		packageTotal: pkgTotal,
		totalSavings: totalSaving,
		savingsBreakdown: breakdown,
		promoDiscount,
		priceAfterDiscount: baseAfterSavings,
		totalTax,
		taxBreakdown: taxMap,
		finalPrice,
	};
};

